import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { loyaltyAPI, clientsAPI, barbersAPI } from '../lib/api';
import { filterClients, normalizeText } from '../lib/clientSearch';
import useDebounce from '../hooks/useDebounce';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import SearchableSelect from '../components/SearchableSelect';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Gift, Plus, Loader2, UserPlus, Copy, ExternalLink, Trash2,
  ShieldCheck, ShieldOff, Percent, Sparkles, User as UserIcon, Stamp,
  History as HistoryIcon, Undo2, Scissors, Users, Search, X,
} from 'lucide-react';
import { toast } from 'sonner';

export default function LealtadPage() {
  const { selectedBusiness, isAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [invitedByBarber, setInvitedByBarber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [tab, setTab] = useState('existing');
  const [submitting, setSubmitting] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [stampsLog, setStampsLog] = useState(null);

  // enroll form
  const [existingClient, setExistingClient] = useState(null);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newBirthday, setNewBirthday] = useState('');

  const loadData = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const [membersRes, clientsRes, barbersRes] = await Promise.all([
        loyaltyAPI.listMembers(),
        clientsAPI.getAll({ limit: 5000 }),
        barbersAPI.getAll({ include_pos_shared: true, exclude_admin_collab: true }),
      ]);
      setMembers(membersRes.data?.members || []);
      setClients(clientsRes.data || []);
      setBarbers(
        (barbersRes.data || []).filter((b) => b.is_active !== false)
      );
    } catch (err) {
      logger.error('Load lealtad', err);
      toast.error('Error al cargar el programa');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetEnrollForm = () => {
    setExistingClient(null);
    setInvitedByBarber(null);
    setNewFirstName(''); setNewLastName(''); setNewPhone(''); setNewBirthday('');
    setTab('existing');
  };

  const handleEnroll = async () => {
    setSubmitting(true);
    try {
      if (!invitedByBarber?.id) {
        toast.error('Selecciona quién invitó al cliente al club');
        setSubmitting(false);
        return;
      }
      const basePayload = { invited_by_barber_id: invitedByBarber.id };
      const payload = tab === 'existing'
        ? { ...basePayload, client_id: existingClient?.id }
        : {
            ...basePayload,
            first_name: newFirstName.trim(),
            last_name: newLastName.trim(),
            phone: newPhone.trim(),
            birthday: newBirthday || null,
          };
      if (tab === 'existing' && !payload.client_id) {
        toast.error('Selecciona un cliente'); setSubmitting(false); return;
      }
      if (tab === 'new' && (!payload.first_name || !payload.phone)) {
        toast.error('Nombre y teléfono son requeridos'); setSubmitting(false); return;
      }
      const { data } = await loyaltyAPI.enroll(payload);
      toast.success(`Cliente inscrito — ID #${String(data.client_number).padStart(4, '0')}`);
      setEnrollOpen(false);
      resetEnrollForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al inscribir');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnenroll = async (member) => {
    if (!window.confirm(`¿Remover a ${member.first_name} ${member.last_name} del programa?`)) return;
    try {
      await loyaltyAPI.unenroll(member.client_id);
      toast.success('Cliente removido del programa');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleManualStamp = async (member) => {
    const currentStamps = member.active_card?.stamps || 0;
    if (currentStamps >= 10) {
      toast.error('La tarjeta ya está completa (10/10). Canjea el servicio gratis para reiniciar.');
      return;
    }
    const label = `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'este cliente';
    if (!window.confirm(`¿Agregar 1 sello a ${label}? Se registrará como sellado manual.`)) return;
    try {
      const { data } = await loyaltyAPI.manualStamp(member.client_id);
      const newStamps = data.active_card?.stamps || currentStamps + 1;
      toast.success(`Sello agregado — ahora ${newStamps}/10`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo sellar');
    }
  };

  const handleOpenLog = async (member) => {
    setStampsLog({ member, entries: [] });
    setLogOpen(true);
    setLogLoading(true);
    try {
      const { data } = await loyaltyAPI.stampsLog(member.client_id);
      setStampsLog({ member, entries: data.stamps || [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar el historial');
    } finally {
      setLogLoading(false);
    }
  };

  const handleUndoLastStamp = async (member) => {
    const currentStamps = member.active_card?.stamps || 0;
    if (currentStamps <= 0) {
      toast.error('No hay sellos que deshacer');
      return;
    }
    const label = `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'este cliente';
    if (!window.confirm(`¿Quitar el último sello de ${label}? Pasará de ${currentStamps} a ${currentStamps - 1}/10.`)) return;
    try {
      const { data } = await loyaltyAPI.undoLastStamp(member.client_id);
      toast.success(`Sello #${data.removed_stamp?.stamp_number} eliminado`);
      loadData();
      // If the log dialog is showing this member, refresh it too
      if (logOpen && stampsLog?.member?.client_id === member.client_id) {
        handleOpenLog(member);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo deshacer');
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/mi-tarjeta/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado al portapapeles', {
      description: 'Comparte este link con el cliente + los últimos 4 dígitos de su teléfono',
    });
  };

  const handleRedeem = async (card, reward_type) => {
    if (!window.confirm(`¿Marcar recompensa ${reward_type === '50' ? '50% descuento' : 'servicio GRATIS'} como canjeada?`)) return;
    try {
      await loyaltyAPI.redeemReward(card.id, reward_type);
      toast.success('Recompensa canjeada');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const enrolledIds = new Set(members.map((m) => m.client_id));
  const availableClients = clients.filter((c) => !enrolledIds.has(c.id));

  // Client name/phone lookup used by the members search below. Members only
  // carry `client_id` + card summary, so we need the clients collection to
  // match names.
  const clientsById = new Map((clients || []).map((c) => [c.id, c]));

  // Filter members by search term (name, phone, email or client id) using
  // the shared, diacritic-insensitive matcher. Debounced to 300 ms.
  const debouncedSearch = useDebounce(search, 300);
  const filteredMembers = (() => {
    const q = normalizeText(debouncedSearch);
    if (!q) return members;
    // Build a virtual "client" per member so we can reuse filterClients.
    const virtual = members.map((m) => {
      const c = clientsById.get(m.client_id) || {};
      return {
        ...c,
        id: m.client_id,
        __member: m,
      };
    });
    const matched = filterClients(virtual, debouncedSearch);
    return matched.map((v) => v.__member);
  })();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lealtad-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Gift className="w-7 h-7" /> Programa de Lealtad
          </h1>
          <p className="text-slate-500 mt-1">
            Inscripción manual — {selectedBusiness?.name}
          </p>
        </div>
        <Button
          onClick={() => setEnrollOpen(true)}
          className="bg-primary text-white hover:bg-primary/90"
          data-testid="enroll-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" /> Inscribir cliente
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <StatCard label="Inscritos" value={members.length} icon={ShieldCheck} color="emerald" />
        <StatCard
          label="Tarjetas activas"
          value={members.filter((m) => m.active_card).length}
          icon={Sparkles}
          color="amber"
        />
        <StatCard
          label="Tarjetas completadas"
          value={members.reduce((n, m) => n + (m.completed_cards || 0), 0)}
          icon={Gift}
          color="pink"
        />
      </div>

      <Card className="border-slate-200 bg-white">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
            Miembros del programa
          </CardTitle>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, teléfono o email…"
              className="pl-9 pr-9"
              data-testid="members-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="members-search-clear"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="empty-members">
              <ShieldOff className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Aún no hay clientes inscritos.</p>
              <p className="text-xs mt-1">Usa &ldquo;Inscribir cliente&rdquo; para agregar el primero.</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="no-members-search-results">
              <Search className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Sin coincidencias para &ldquo;{search}&rdquo;</p>
              <p className="text-xs mt-1">Prueba con nombre, teléfono o email.</p>
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              <div className="mb-2 text-xs text-slate-500">
                Mostrando {filteredMembers.length} de {members.length} miembro(s)
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead className="text-center">Sellos</TableHead>
                    <TableHead className="text-center">Recompensas</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.map((m) => {
                    const c = m.active_card;
                    const stamps = c?.stamps || 0;
                    const has50 = c?.reward_50_earned_at && !c?.reward_50_redeemed_at;
                    const hasFree = c?.reward_free_earned_at && !c?.reward_free_redeemed_at;
                    return (
                      <TableRow key={m.client_id} data-testid={`member-row-${m.client_id}`}>
                        <TableCell className="font-mono text-xs">
                          #{String(m.client_number || 0).padStart(4, '0')}
                        </TableCell>
                        <TableCell className="font-medium">
                          {m.first_name} {m.last_name}
                          {m.completed_cards > 0 && (
                            <div className="text-[10px] text-slate-400">
                              {m.completed_cards} tarjeta{m.completed_cards !== 1 ? 's' : ''} completada{m.completed_cards !== 1 ? 's' : ''}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{m.phone || '—'}</TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-slate-100 text-slate-700 border-slate-200 font-mono">
                            {stamps} / 10
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center space-x-1">
                          {has50 && (
                            <Button size="sm" variant="outline"
                              className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => handleRedeem(c, '50')}
                              data-testid={`redeem-50-${m.client_id}`}
                            >
                              <Percent className="w-3 h-3 mr-1" /> Canjear 50%
                            </Button>
                          )}
                          {hasFree && (
                            <Button size="sm" variant="outline"
                              className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleRedeem(c, 'free')}
                              data-testid={`redeem-free-${m.client_id}`}
                            >
                              <Gift className="w-3 h-3 mr-1" /> Canjear GRATIS
                            </Button>
                          )}
                          {!has50 && !hasFree && (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600 hover:bg-emerald-50"
                            title="Sellar manualmente (+1 sello)"
                            onClick={() => handleManualStamp(m)}
                            data-testid={`manual-stamp-${m.client_id}`}
                          >
                            <Stamp className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8"
                            title="Ver historial de sellos"
                            onClick={() => handleOpenLog(m)}
                            data-testid={`stamp-log-${m.client_id}`}
                          >
                            <HistoryIcon className="w-4 h-4" />
                          </Button>
                          {isAdmin() && (
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-700 hover:bg-amber-50"
                              title="Quitar último sello (Master Admin)"
                              onClick={() => handleUndoLastStamp(m)}
                              disabled={(m.active_card?.stamps || 0) === 0}
                              data-testid={`undo-stamp-${m.client_id}`}
                            >
                              <Undo2 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-8 w-8"
                            title="Copiar link"
                            onClick={() => copyLink(m.access_token)}
                            data-testid={`copy-link-${m.client_id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8"
                            title="Abrir tarjeta"
                            onClick={() => window.open(`/mi-tarjeta/${m.access_token}`, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:bg-red-50"
                            title="Remover del programa"
                            onClick={() => handleUnenroll(m)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enroll dialog */}
      <Dialog open={enrollOpen} onOpenChange={(o) => { setEnrollOpen(o); if (!o) resetEnrollForm(); }}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" /> Inscribir cliente
            </DialogTitle>
            <DialogDescription>
              Selecciona un cliente existente o crea uno nuevo. Se generará un ID único y un link para acceder a su tarjeta.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="existing" data-testid="tab-existing">Cliente existente</TabsTrigger>
              <TabsTrigger value="new" data-testid="tab-new">Cliente nuevo</TabsTrigger>
            </TabsList>

            <TabsContent value="existing" className="mt-4 space-y-3">
              <Label>Buscar cliente</Label>
              <SearchableSelect
                items={availableClients}
                value={existingClient?.id || ''}
                onChange={(item) => setExistingClient(item)}
                placeholder="Buscar por nombre o teléfono…"
                emptyLabel="Sin coincidencias"
                getLabel={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Sin nombre'}
                getSubLabel={(c) => c.phone || c.email || ''}
                searchFields={(c) => [c.first_name, c.last_name, `${c.first_name || ''} ${c.last_name || ''}`, c.phone, c.email]}
                testId="existing-client-select"
              />
              {availableClients.length === 0 && (
                <p className="text-xs text-slate-500">
                  Todos los clientes ya están inscritos. Usa la pestaña &ldquo;Cliente nuevo&rdquo;.
                </p>
              )}
            </TabsContent>            <TabsContent value="new" className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Nombre *</Label>
                  <Input value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)}
                    className="bg-slate-100 border-slate-200"
                    data-testid="new-first-name" />
                </div>
                <div className="space-y-1">
                  <Label>Apellido</Label>
                  <Input value={newLastName} onChange={(e) => setNewLastName(e.target.value)}
                    className="bg-slate-100 border-slate-200"
                    data-testid="new-last-name" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Teléfono *</Label>
                <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
                  className="bg-slate-100 border-slate-200"
                  placeholder="10 dígitos"
                  data-testid="new-phone" />
                <p className="text-[11px] text-slate-500">
                  El cliente usará los últimos 4 dígitos para acceder a su tarjeta.
                </p>
              </div>
              <div className="space-y-1">
                <Label>Fecha de cumpleaños</Label>
                <Input type="date" value={newBirthday} onChange={(e) => setNewBirthday(e.target.value)}
                  className="bg-slate-100 border-slate-200"
                  data-testid="new-birthday" />
              </div>
            </TabsContent>
          </Tabs>

          {/* Who invited the client — mandatory */}
          <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
            <Label className="flex items-center gap-1 font-medium">
              ¿Quién lo invitó al club? <span className="text-rose-500">*</span>
            </Label>
            <SearchableSelect
              items={barbers}
              value={invitedByBarber?.id || ''}
              onChange={(b) => setInvitedByBarber(b)}
              placeholder="Buscar barbero o estilista…"
              emptyLabel="Sin empleados activos"
              getLabel={(b) => b.display_name}
              getSubLabel={(b) => b.profile_type === 'stylist' ? 'Estilista' : (b.profile_type === 'admin_collaborator' ? 'Colaborador' : 'Barbero')}
              searchFields={(b) => [b.display_name]}
              testId="invited-by-barber-select"
            />
            <p className="text-[11px] text-slate-500">
              Quedará registrado como responsable de la inscripción en el perfil del cliente.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollOpen(false)} disabled={submitting}>Cancelar</Button>
            <Button
              onClick={handleEnroll}
              disabled={submitting || !invitedByBarber?.id}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="submit-enroll"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Inscribir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stamps log dialog */}
      <Dialog open={logOpen} onOpenChange={(v) => { setLogOpen(v); if (!v) setStampsLog(null); }}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[85vh] overflow-hidden p-0" data-testid="stamps-log-dialog">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Historial de sellos</DialogTitle>
            {stampsLog?.member && (
              <p className="text-sm text-slate-500 mt-1">
                {stampsLog.member.first_name} {stampsLog.member.last_name}
                {stampsLog.entries?.length ? <> · <strong>{stampsLog.entries.length}</strong> movimiento(s)</> : null}
              </p>
            )}
          </DialogHeader>
          <div className="overflow-y-auto max-h-[calc(85vh-140px)] px-6 pb-6">
            {logLoading ? (
              <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (stampsLog?.entries?.length || 0) === 0 ? (
              <div className="py-10 text-center text-sm text-slate-400">Este cliente aún no tiene sellos registrados.</div>
            ) : (
              <div className="border border-slate-200 rounded-md overflow-hidden mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs text-slate-500">
                      <th className="px-3 py-2 font-medium">#</th>
                      <th className="px-3 py-2 font-medium">Tipo</th>
                      <th className="px-3 py-2 font-medium">Fecha</th>
                      <th className="px-3 py-2 font-medium">Hora</th>
                      <th className="px-3 py-2 font-medium">Por</th>
                      <th className="px-3 py-2 font-medium">Ticket</th>
                    </tr>
                  </thead>
                  <tbody data-testid="stamps-log-rows">
                    {stampsLog.entries.map((s) => {
                      const dt = new Date(s.created_at);
                      const fecha = dt.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Mexico_City' });
                      const hora = dt.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/Mexico_City' });
                      const badgeColor = s.source === 'referral' ? 'bg-sky-100 text-sky-800 border-sky-200'
                        : s.source === 'manual' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                        : 'bg-amber-100 text-amber-800 border-amber-200';
                      const Icon = s.source === 'referral' ? Users : s.source === 'manual' ? Stamp : Scissors;
                      const label = s.source === 'referral' ? 'Referido' : s.source === 'manual' ? 'Manual' : 'Venta';
                      return (
                        <tr key={s.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-mono text-slate-500">#{s.stamp_number}</td>
                          <td className="px-3 py-2">
                            <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium', badgeColor)}>
                              <Icon className="w-3 h-3" /> {label}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-700 whitespace-nowrap">{fecha}</td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-700 whitespace-nowrap">{hora}</td>
                          <td className="px-3 py-2 text-slate-700">{s.actor_name}</td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-500">{s.sale_ticket_number || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter className="p-6 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={() => setLogOpen(false)} className="border-slate-200">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  const colorMap = {
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
    pink: 'text-pink-600 bg-pink-50',
  };
  return (
    <Card className="border-slate-200 bg-white">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', colorMap[color])}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs uppercase text-slate-500 tracking-wide">{label}</p>
          <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
